import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { UIButton, UIInlineAlert, UISpacer, UIText, UIToast } from '@/ui';
import { SsoConsent, useSsoLoginAction } from '../actions';
import {
    SsoCancelledError,
    SsoIdToken,
    SsoProviderId,
    getEnabledSsoProviders,
    getSsoAdapterKind,
    mountSsoButton,
    signInWithSso
} from '../util';
import { useMeTranslation } from '../hooks';
import { SsoConsentModal } from './sso-consent-modal';
import styles from './sso-sign-in.module.scss';

export type SsoSignInProps = {
    /** Disables the buttons while the surrounding form is busy. */
    disabled?: boolean;
    onSuccess?: () => void;
};

type PendingConsent = {
    provider: SsoProviderId;
    token: SsoIdToken;
};

/**
 * The whole SSO flow — buttons, the consent step and the errors from both —
 * so a page only has to place it. Renders nothing when no provider has a client
 * id in this build.
 */
export function SsoSignIn({
    disabled = false,
    onSuccess
}: SsoSignInProps): ReactElement | null {
    const { t } = useMeTranslation();

    const providers = useMemo(() => getEnabledSsoProviders(), []);

    // The sign-in that came back asking for consent. Its token is held here so
    // the retry can replay it, rather than sending the user through the
    // provider a second time.
    const [pendingConsent, setPendingConsent] = useState<PendingConsent | null>(
        null
    );
    // Failures from the provider itself — a blocked popup, a script that would
    // not load — which never reach the API.
    const [providerError, setProviderError] = useState('');

    const {
        ssoLogin,
        loading,
        pendingProvider,
        error: ssoError,
        clearError: clearSsoError
    } = useSsoLoginAction({
        onConsentRequired: setPendingConsent,
        // Nothing can be retried from here: the browser cannot mint a
        // replacement token without another click, so close the modal and let
        // the user start again from the buttons.
        onTokenRejected: () => setPendingConsent(null)
    });

    if (providers.length === 0) {
        return null;
    }

    const runSsoLogin = (
        provider: SsoProviderId,
        token: SsoIdToken,
        consent?: SsoConsent
    ): void => {
        setProviderError('');

        ssoLogin({ provider, token, consent })
            .then(() => {
                setPendingConsent(null);
                onSuccess?.();
            })
            .catch(() => {}); // Handled in the hook
    };

    const displayedError = ssoError || providerError;

    return (
        <>
            <UISpacer multiplier={2} />

            {displayedError && (
                <>
                    <UIInlineAlert
                        text={displayedError}
                        onClose={() => {
                            clearSsoError();
                            setProviderError('');
                        }}
                    />
                    <UISpacer multiplier={2} />
                </>
            )}

            <SsoSignInButtons
                pendingProvider={pendingProvider}
                disabled={disabled}
                onIdToken={(provider, token) => runSsoLogin(provider, token)}
                onError={(error) => {
                    console.error('SSO provider error', error);

                    const message = t(
                        'components.sso_sign_in.error_provider_failed'
                    );
                    UIToast.error(message);
                    setProviderError(message);
                }}
            />

            <SsoConsentModal
                isOpen={pendingConsent !== null}
                loading={loading}
                onClose={() => setPendingConsent(null)}
                onSubmit={(consent) => {
                    if (pendingConsent) {
                        runSsoLogin(
                            pendingConsent.provider,
                            pendingConsent.token,
                            consent
                        );
                    }
                }}
            />
        </>
    );
}

type SsoSignInButtonsProps = {
    /** The provider currently being exchanged with the API, if any. */
    pendingProvider: SsoProviderId | null;
    disabled?: boolean;
    onIdToken: (provider: SsoProviderId, token: SsoIdToken) => void;
    onError: (error: unknown) => void;
};

function Divider(): ReactElement {
    const { t } = useMeTranslation();

    return (
        <div className={styles.divider}>
            <div className={styles.dividerRule} />
            <UIText variant="small">
                {t('components.sso_sign_in.divider')}
            </UIText>
            <div className={styles.dividerRule} />
        </div>
    );
}

/**
 * One button per provider that has a client id in this build, so an
 * unconfigured provider is hidden rather than shown failing.
 */
export function SsoSignInButtons({
    pendingProvider,
    disabled = false,
    onIdToken,
    onError
}: SsoSignInButtonsProps): ReactElement | null {
    const { t } = useMeTranslation();

    const providers = useMemo(() => getEnabledSsoProviders(), []);

    // The provider whose own flow is open. The popup outlives the press, so
    // this is separate from the exchange that follows it.
    const [acquiringProvider, setAcquiringProvider] =
        useState<SsoProviderId | null>(null);

    if (providers.length === 0) {
        return null;
    }

    const busyProvider = acquiringProvider ?? pendingProvider;

    const handleError = (error: unknown): void => {
        // Backing out of the provider's popup is not a failure.
        if (error instanceof SsoCancelledError) {
            return;
        }

        onError(error);
    };

    const onImperativePress = (provider: SsoProviderId): void => {
        setAcquiringProvider(provider);

        signInWithSso(provider)
            .then((token) => {
                onIdToken(provider, token);
            })
            .catch(handleError)
            .finally(() => {
                setAcquiringProvider(null);
            });
    };

    return (
        <div className={styles.container}>
            <Divider />

            {providers.map((provider) => {
                const isBusy = busyProvider === provider;
                const isDisabled =
                    disabled || (busyProvider !== null && !isBusy);

                return (
                    <div key={provider}>
                        <UISpacer multiplier={2} />

                        {getSsoAdapterKind(provider) === 'rendered' ? (
                            <SsoRenderedButton
                                provider={provider}
                                disabled={isDisabled || isBusy}
                                onIdToken={(token) =>
                                    onIdToken(provider, token)
                                }
                                onError={handleError}
                            />
                        ) : (
                            <UIButton
                                variant="default"
                                loading={isBusy}
                                disabled={isDisabled}
                                fullWidth
                                onClick={() => onImperativePress(provider)}
                            >
                                <span className={styles.buttonContent}>
                                    <span className={styles.buttonLogo}>
                                        <SsoProviderLogo provider={provider} />
                                    </span>
                                    <span className={styles.buttonLabel}>
                                        {t(
                                            'components.sso_sign_in.continue_with',
                                            {
                                                provider: t(
                                                    `components.sso_sign_in.providers.${provider}`
                                                )
                                            }
                                        )}
                                    </span>
                                </span>
                            </UIButton>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

type SsoRenderedButtonProps = {
    provider: SsoProviderId;
    disabled: boolean;
    onIdToken: (token: SsoIdToken) => void;
    onError: (error: unknown) => void;
};

/**
 * A button the provider draws itself. Google needs one — it will not hand over
 * an ID token from a button we control — so the width has to be measured and
 * fed back to it, and re-fed whenever the container changes size.
 */
export function SsoRenderedButton({
    provider,
    disabled,
    onIdToken,
    onError
}: SsoRenderedButtonProps): ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    // Callbacks are held in refs so a re-render does not tear the provider's
    // button down and rebuild it.
    const onIdTokenRef = useRef(onIdToken);
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onIdTokenRef.current = onIdToken;
        onErrorRef.current = onError;
    });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const observer = new ResizeObserver(([entry]) => {
            setWidth(entry.contentRect.width);
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || width === 0) {
            return;
        }

        return mountSsoButton(provider, container, {
            width,
            onIdToken: (token) => onIdTokenRef.current(token),
            onError: (error) => onErrorRef.current(error)
        });
    }, [provider, width]);

    return (
        <div
            className={disabled ? styles.renderedDisabled : undefined}
            aria-disabled={disabled}
        >
            <div ref={containerRef} />
        </div>
    );
}

type SsoProviderLogoProps = {
    provider: SsoProviderId;
    size?: number;
};

function MicrosoftLogo({ size }: { size: number }): ReactElement {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
            <rect x="1" y="1" width="21" height="21" fill="#F25022" />
            <rect x="26" y="1" width="21" height="21" fill="#7FBA00" />
            <rect x="1" y="26" width="21" height="21" fill="#00A4EF" />
            <rect x="26" y="26" width="21" height="21" fill="#FFB900" />
        </svg>
    );
}

/**
 * Logos for the providers we render a button for. Google is absent on purpose:
 * its button is rendered by Google and carries its own mark.
 */
export function SsoProviderLogo({
    provider,
    size = 18
}: SsoProviderLogoProps): ReactElement | null {
    if (provider === 'microsoft') {
        return <MicrosoftLogo size={size} />;
    }

    return null;
}
